import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Icons } from '../constants';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useFirebase } from '../context/FirebaseContext';
import { JobStatus } from '../arborlink/types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const PostFeelerJobModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { user } = useFirebase();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [payRate, setPayRate] = useState('');
  const [skills, setSkills] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    setIsSubmitting(true);
    try {
      const jobData = {
        title,
        description,
        location,
        payRate,
        requiredSkills: skills.split(',').map(s => s.trim()).filter(s => s),
        requiredCerts: [],
        companyId: user.uid,
        companyName: user.displayName || 'GreenTree Company',
        date: 'Flexible/TBD',
        status: JobStatus.FEELER,
        applicants: [],
        createdAt: serverTimestamp()
      };
      
      await addDoc(collection(db, 'arborlink_jobs'), jobData);
      onClose();
    } catch (error) {
      console.error('Failed to post feeler job', error);
      alert('Error posting feeler job');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-bark/60 backdrop-blur-sm shadow-xl">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="bg-white rounded-[32px] shadow-2xl w-full max-w-xl overflow-hidden"
        >
          <div className="bg-leaf p-6 text-bark flex justify-between items-center relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-20 transform scale-[2]"><Icons.Lead /></div>
            <div className="relative z-10">
              <h2 className="text-2xl font-black mb-1">Post a Feeler</h2>
              <p className="text-sm font-bold opacity-80 uppercase tracking-widest">Gauge Resource Availability</p>
            </div>
            <button onClick={onClose} className="p-2 bg-white/30 hover:bg-white/40 rounded-xl transition relative z-10">
              <Icons.Close className="w-5 h-5 text-bark" />
            </button>
          </div>
          
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Opportunity Title</label>
                <input 
                  type="text" 
                  required
                  placeholder="E.g., Craning 3 Pines in Columbus"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  className="w-full bg-stone-50 border-2 border-stone-100 p-4 rounded-2xl font-bold text-bark focus:outline-none focus:border-leaf transition"
                />
              </div>

              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Location</label>
                <input 
                  type="text" 
                  required
                  placeholder="City, State"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  className="w-full bg-stone-50 border-2 border-stone-100 p-4 rounded-2xl font-bold text-bark focus:outline-none focus:border-leaf transition"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Pay Rate / Budget</label>
                  <input 
                    type="text" 
                    placeholder="E.g., $300/day or TBD"
                    value={payRate}
                    onChange={e => setPayRate(e.target.value)}
                    className="w-full bg-stone-50 border-2 border-stone-100 p-4 rounded-2xl font-bold text-bark focus:outline-none focus:border-leaf transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Key Skills Needed</label>
                  <input 
                    type="text" 
                    placeholder="E.g., Crane Operator"
                    value={skills}
                    onChange={e => setSkills(e.target.value)}
                    className="w-full bg-stone-50 border-2 border-stone-100 p-4 rounded-2xl font-bold text-bark focus:outline-none focus:border-leaf transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Description / Scope Overview</label>
                <textarea 
                  required
                  rows={3}
                  placeholder="Provide minimal details to gauge interest without committing to a schedule..."
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className="w-full bg-stone-50 border-2 border-stone-100 p-4 rounded-2xl font-medium text-bark focus:outline-none focus:border-leaf transition custom-scrollbar"
                />
              </div>

              <button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full py-4 bg-gecko text-white rounded-2xl font-black tracking-widest uppercase hover:bg-palm transition disabled:opacity-50 mt-4 shadow-md shadow-gecko/20"
              >
                {isSubmitting ? 'Posting...' : 'Post Feeler'}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
